import React from 'react';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  interval,
  map,
  of,
  retry,
  Subject,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import { connectivityService } from '@neptune/shared/core-connection-domain';

import { DocumentVisibility } from '../document-visibility';
import { trackChangesWhenInactive } from '../track-changes-when-inactive';

import { handleFetch } from './handle-fetch';
import { tempo } from './tempo';

type RetryBackoffConfig = {
  backoffDelay?: (iteration: number, initialInterval: number) => number;
  initialInterval?: number;
  maxInterval?: number;
  maxRetries?: number;
};

export type UsePollingParams<T, R> = {
  resolver: (query: T) => Promise<R>;
  pollingInterval?: number | 'fetch_once'; // Pass false to only fetch once.
  weakTriggersThrottleTimeMs?: number;
  onFetchSucceed?: (value: R, query: T) => void;
  onFetchStarted?: (query: T) => void;
  onFetchFailed?: (value: any | undefined, query: T) => void;
  onFetchAborted?: () => void;
  onAfterRetriesError?: (value: any | undefined, query: T) => void;
  onStart?: (query: T) => void;
  enabled?: boolean;
  isPaused?: boolean;
};

export type UsePollingResult<R> = {
  result: R | undefined;
  refresh: () => void;
};

export function defaultBackoffDelay(iteration: number, initialInterval: number) {
  const baseDelay = Math.pow(Math.E, iteration) * initialInterval;
  const jitter = baseDelay * 0.2;

  return baseDelay + Math.random() * jitter * 2 - jitter;
}

export const usePolling = <T, R>(
  query: T,
  {
    resolver,
    pollingInterval = 30_000,
    /**
     * Time in ms that is used as debounce time for the window visibility
     * and connectivity change events.
     */
    weakTriggersThrottleTimeMs = 30_000,
    onFetchStarted,
    onFetchSucceed,
    onFetchFailed,
    onFetchAborted,
    onAfterRetriesError,
    onStart,
    enabled = true,
    isPaused = false,
  }: UsePollingParams<T, R>,
  {
    backoffDelay = defaultBackoffDelay,
    initialInterval = 250,
    maxInterval = 60_000,
    maxRetries = 100,
  }: RetryBackoffConfig = {},
): UsePollingResult<R> => {
  const [result, setResult] = React.useState<R>();

  const query$ = React.useRef(new BehaviorSubject(query));
  const isPaused$ = React.useRef(new BehaviorSubject(isPaused));

  const refresh$ = React.useRef(new Subject<void>());

  const refresh = React.useCallback(() => refresh$.current.next(), [refresh$]);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const polling$ = pollingInterval === 'fetch_once' ? of(0) : interval(pollingInterval);

    // implementation:
    // * every query change
    // * resets tempo (which emits with changed query, with interval, with manual refresh and with application visibility change)
    // * initiates fetch (with lifecycle callbacks) dropping any earlier
    // * in case of error retry fetch with sophisticated backoff settings
    const subscription = query$.current
      .pipe(
        tap<T>((query) => onStart?.(query)),
        tempo(polling$, refresh$.current, 50, weakTriggersThrottleTimeMs),
        map((query) => ({ query, timestamp: Date.now() })),
        trackChangesWhenInactive(isPaused$.current),
        switchMap(({ query }) =>
          of(query).pipe(
            handleFetch({
              resolver,
              onFetchStarted,
              onFetchSucceed,
              onFetchFailed,
            }),
            retry({
              count: maxRetries,
              delay: (error, retryCount) => {
                if (DocumentVisibility.isAppHidden() || connectivityService.isLocked) {
                  return EMPTY;
                }

                return timer(Math.min(backoffDelay(retryCount, initialInterval), maxInterval));
              },
            }),
            catchError((err) => {
              onAfterRetriesError?.(err, query);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(setResult);

    return () => {
      subscription.unsubscribe();
      onFetchAborted?.();
    };
  }, [
    resolver,
    pollingInterval,
    backoffDelay,
    initialInterval,
    maxInterval,
    maxRetries,
    onFetchStarted,
    weakTriggersThrottleTimeMs,
    onFetchSucceed,
    onFetchFailed,
    onFetchAborted,
    onAfterRetriesError,
    onStart,
    enabled,
  ]);

  React.useEffect(() => {
    isPaused$.current.next(isPaused);
  }, [isPaused]);

  React.useEffect(() => {
    if (enabled) {
      query$.current.next(query);
    }
  }, [enabled, query]);

  return {
    result,
    refresh,
  };
};
